import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteTenant } from '../../services/apiSuperAdmin';

export default function ForceDeleteModal({ isOpen, onClose, tenant }) {
    const qc = useQueryClient();
    const [confirmText, setConfirmText] = useState('');

    const deleteMut = useMutation({
        mutationFn: () => deleteTenant(tenant.id, confirmText),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['super-admin-tenants'] });
            handleClose();
        },
    });

    const handleClose = () => {
        setConfirmText('');
        onClose();
    };

    if (!isOpen || !tenant) return null;

    const isClone = !!tenant.cloned_from_tenant_id;
    const hasClones = tenant.clones_count > 0;
    const isExactMatch = confirmText === tenant.name;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="bg-red-50 dark:bg-red-950/50 border-b border-red-100 dark:border-red-900/50 px-7 py-5">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-red-500 text-[24px]">delete_forever</span>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-red-800 dark:text-red-200">Eliminar Tenant</h2>
                            <div className="flex items-center gap-2 mt-0.5">
                                <p className="text-xs text-red-600 dark:text-red-400 font-medium">{tenant.name}</p>
                                {isClone && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                                        <span className="material-symbols-outlined text-[11px]">content_copy</span>
                                        CLON
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-7 space-y-5">
                    {/* Block if canonical with active clones */}
                    {hasClones ? (
                        <div className="space-y-4">
                            <div className="p-4 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
                                <div className="flex gap-2">
                                    <span className="material-symbols-outlined text-orange-500 text-[20px] shrink-0 mt-0.5">block</span>
                                    <div>
                                        <p className="text-sm font-bold text-orange-800 dark:text-orange-300">Eliminación bloqueada</p>
                                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                            Este tenant es un tenant <strong>canónico</strong> que aún tiene clones activos.
                                            Para proteger la integridad de los datos, debes eliminar todos sus clones primero.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={handleClose}
                                className="w-full py-2.5 rounded-xl text-sm font-medium border border-gray-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            >
                                Entendido, cerrar
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Info about what gets deleted */}
                            <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50">
                                <p className="text-xs font-bold text-red-700 dark:text-red-400 mb-2 flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[15px]">warning</span>
                                    Esta acción eliminará permanentemente:
                                </p>
                                <ul className="text-xs text-red-600 dark:text-red-400 space-y-1 ml-5 list-disc">
                                    <li>El tenant y toda su configuración</li>
                                    <li>Todos los usuarios ({tenant.users_count ?? 0})</li>
                                    <li>Todos los contactos, leads y conversaciones</li>
                                    <li>Estrategias de IA, webhooks, API configs</li>
                                    <li>Plantillas, campos personalizados, etapas</li>
                                    <li>Conexiones de WhatsApp e Instagram</li>
                                </ul>
                            </div>

                            {isClone && (
                                <div className="p-3 rounded-xl bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
                                    <p className="text-xs text-violet-700 dark:text-violet-400 flex gap-2">
                                        <span className="material-symbols-outlined text-[15px] shrink-0">info</span>
                                        El tenant original <strong>no será afectado</strong> por esta eliminación.
                                    </p>
                                </div>
                            )}

                            {/* Confirmation text input */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                    Para confirmar, escribe el nombre exacto del tenant:
                                </label>
                                <p className="text-xs font-mono text-slate-500 dark:text-slate-400 mb-2 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-lg inline-block">
                                    {tenant.name}
                                </p>
                                <input
                                    type="text"
                                    placeholder="Escribe el nombre exacto..."
                                    value={confirmText}
                                    onChange={e => setConfirmText(e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-colors bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 ${
                                        isExactMatch
                                            ? 'border-red-400 focus:ring-red-500/30'
                                            : 'border-gray-200 dark:border-slate-700 focus:ring-red-400/30 focus:border-red-300'
                                    }`}
                                />
                            </div>

                            {deleteMut.isError && (
                                <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                    <p className="text-xs text-red-600 dark:text-red-400">
                                        {deleteMut.error?.response?.data?.message || 'Error al eliminar el tenant.'}
                                    </p>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    onClick={handleClose}
                                    disabled={deleteMut.isPending}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-gray-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={() => deleteMut.mutate()}
                                    disabled={!isExactMatch || deleteMut.isPending}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900/40 disabled:cursor-not-allowed text-white shadow-lg shadow-red-500/20 transition-all flex items-center justify-center gap-2"
                                >
                                    {deleteMut.isPending ? (
                                        <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Eliminando...</>
                                    ) : (
                                        <><span className="material-symbols-outlined text-[18px]">delete_forever</span> Eliminar definitivamente</>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
